import {
  faArrowRight,
  faBolt,
  faCameraRetro,
  faCheck,
  faCloudArrowUp,
  faDownload,
  faImages,
  faQrcode,
  faShareNodes,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";

import rooftopEvent from "../../assets/snappin-rooftop-event.png";
import { useLanguage } from "../../contexts/LanguageContext";

const copy = {
  es: {
    eyebrow: "El carrete compartido de tu evento",
    secondary: "Ver cómo funciona",
    proof: "Sin app. Sin registro para invitados. Todo en un solo lugar.",
    live: "Ahora mismo",
    uploaded: "Lola subió 8 fotos",
    moments: "Todos los ángulos. Un solo álbum.",
    eventName: "Noche de Lola",
    albumLive: "Álbum en vivo",
    scan: "Escanea para sumarte",
    ticker: ["Bodas", "Cumples", "Festivales", "Viajes", "Graduaciones", "After offices"],
    howEyebrow: "Así de fácil",
    howTitle: "Crea el evento. Ellos hacen el resto.",
    howBody: "Un enlace convierte cada teléfono en una cámara para tu álbum. Tú conservas el control.",
    productEyebrow: "Tu cabina de control",
    productTitle: "Mientras ellos viven el momento, Snappin junta la historia.",
    productBody: "Mira quién participa, recibe fotos en tiempo real y descarga el álbum completo cuando quieras.",
    photoLabel: "Fotos recibidas",
    guestLabel: "Personas compartiendo",
    featuresEyebrow: "Hecho para el momento",
    featuresTitle: "Menos coordinación. Más fotos que nadie sabía que existían.",
    privateTitle: "Privado por diseño",
    privateBody: "Solo entra quien tiene tu enlace o QR.",
    instantTitle: "Cero descargas",
    instantBody: "Abre la cámara, sube y vuelve a la fiesta.",
    ownTitle: "Tus recuerdos, tuyos",
    ownBody: "Organiza y descarga el álbum completo.",
    aiEyebrow: "Edición con IA",
    aiTitle: "Ese fotón solo necesitaba un pequeño empujón.",
    aiBody: "Corrige luz y color, aplica un look o describe el resultado. Directo desde tu galería.",
    aiCta: "Probar en mi evento",
    galleryEyebrow: "Desde cualquier teléfono",
    galleryTitle: "Diseñado para que nadie tenga que preguntar cómo se usa.",
  },
  en: {
    eyebrow: "The shared camera roll for your event",
    secondary: "See how it works",
    proof: "No app. No guest accounts. Everything in one place.",
    live: "Right now",
    uploaded: "Lola uploaded 8 photos",
    moments: "Every angle. One shared album.",
    eventName: "Lola's night",
    albumLive: "Live album",
    scan: "Scan to join",
    ticker: ["Weddings", "Birthdays", "Festivals", "Trips", "Graduations", "After work"],
    howEyebrow: "That simple",
    howTitle: "Create the event. They do the rest.",
    howBody: "One link turns every phone into a camera for your album. You stay in control.",
    productEyebrow: "Your control room",
    productTitle: "While they live the moment, Snappin collects the story.",
    productBody: "See who joins, receive photos live and download the complete album whenever you want.",
    photoLabel: "Photos received",
    guestLabel: "People sharing",
    featuresEyebrow: "Made for the moment",
    featuresTitle: "Less coordinating. More photos you never knew existed.",
    privateTitle: "Private by design",
    privateBody: "Only people with your link or QR get in.",
    instantTitle: "Zero downloads",
    instantBody: "Open, upload and get back to the party.",
    ownTitle: "Your memories, yours",
    ownBody: "Organize and download the full album.",
    aiEyebrow: "AI editing",
    aiTitle: "That great shot just needed a little nudge.",
    aiBody: "Fix light and color, apply a look or describe the result. Right from your gallery.",
    aiCta: "Try it on my event",
    galleryEyebrow: "From any phone",
    galleryTitle: "Designed so nobody has to ask how it works.",
  },
};

const HeroOne = () => {
  const { t, language } = useLanguage();
  const c = copy[language] || copy.en;
  const steps = [
    { icon: faCameraRetro, title: t("howToWorks.steps.0.title"), description: t("howToWorks.steps.0.description") },
    { icon: faQrcode, title: t("howToWorks.steps.1.title"), description: t("howToWorks.steps.1.description") },
    { icon: faImages, title: t("howToWorks.steps.2.title"), description: t("howToWorks.steps.2.description") },
  ];

  return (
    <main className="snappin-home">
      <section className="sn-hero">
        <div className="sn-hero-copy">
          <div className="sn-kicker"><span />{c.eyebrow}</div>
          <h1>{t("heroHome.title")} <em>{t("heroHome.subtitle")}</em></h1>
          <p>{t("heroHome.description")} {t("heroHome.subDescription")}</p>
          <div className="sn-hero-actions">
            <Link to="/create-event" className="sn-button sn-button-primary">{t("heroHome.createEvent")} <FontAwesomeIcon icon={faArrowRight} /></Link>
            <a href="#how-it-works" className="sn-button sn-button-quiet">{c.secondary}</a>
          </div>
          <div className="sn-proof"><FontAwesomeIcon icon={faCheck} /> {c.proof}</div>
        </div>
        <div className="sn-hero-visual" aria-label="Snappin live event album preview">
          <div className="sn-album-shell">
            <div className="sn-album-bar">
              <div><span className="sn-live-dot" />{c.albumLive}</div>
              <span>148</span>
            </div>
            <div className="sn-album-title"><small>{c.eventName}</small><strong>{c.moments}</strong></div>
            <div className="sn-album-grid">
              <figure className="sn-album-main"><img src={rooftopEvent} alt="Friends sharing a moment at a rooftop event" /></figure>
              <figure className="sn-album-crop sn-album-crop-one"><img src={rooftopEvent} alt="Candid rooftop event moment" /></figure>
              <figure className="sn-album-crop sn-album-crop-two"><img src={rooftopEvent} alt="Guests celebrating together" /></figure>
            </div>
          </div>
          <div className="sn-live-card"><span className="sn-avatar">L</span><div><small>{c.live}</small><strong>{c.uploaded}</strong></div><span className="sn-upload-check"><FontAwesomeIcon icon={faCheck} /></span></div>
          <div className="sn-join-card"><FontAwesomeIcon icon={faQrcode} /><span>{c.scan}</span></div>
        </div>
      </section>

      <div className="sn-ticker" aria-hidden="true"><div>{[...c.ticker, ...c.ticker].map((item, index) => <span key={`${item}-${index}`}>{item}<i /></span>)}</div></div>

      <section className="sn-section sn-how" id="how-it-works">
        <div className="sn-section-intro">
          <span className="sn-section-index">01</span>
          <div><p className="sn-eyebrow">{c.howEyebrow}</p><h2>{c.howTitle}</h2></div>
          <p className="sn-section-copy">{c.howBody}</p>
        </div>
        <div className="sn-steps">
          {steps.map((step, index) => <article className="sn-step" key={step.title} style={{ "--step": index }}><div className="sn-step-top"><span>0{index + 1}</span><FontAwesomeIcon icon={step.icon} /></div><h3>{step.title}</h3><p>{step.description}</p></article>)}
        </div>
      </section>

      <section className="sn-section sn-product">
        <div className="sn-product-copy"><p className="sn-eyebrow">{c.productEyebrow}</p><h2>{c.productTitle}</h2><p>{c.productBody}</p><div className="sn-mini-stats"><div><strong>148</strong><span>{c.photoLabel}</span></div><div><strong>37</strong><span>{c.guestLabel}</span></div></div></div>
        <div className="sn-dashboard-frame">
          <div className="sn-browser-bar"><i/><i/><i/><span>snappin.cloud</span></div>
          <div className="sn-demo-dashboard">
            <aside><b>S</b><i/><i/><i/></aside>
            <div className="sn-demo-main">
              <div className="sn-demo-head"><div><small>{c.albumLive}</small><strong>{c.eventName}</strong></div><button><FontAwesomeIcon icon={faShareNodes} /></button></div>
              <div className="sn-demo-stats"><span><b>148</b>{c.photoLabel}</span><span><b>37</b>{c.guestLabel}</span><div><FontAwesomeIcon icon={faCloudArrowUp} /> +8</div></div>
              <div className="sn-demo-gallery">{["16%","35%","54%","72%","88%"].map((position,index) => <figure key={position} className={index === 0 ? "sn-demo-tall" : ""}><img src={rooftopEvent} alt="" style={{ objectPosition: `${position} center` }} /></figure>)}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="sn-section sn-features">
        <div className="sn-feature-heading"><p className="sn-eyebrow">{c.featuresEyebrow}</p><h2>{c.featuresTitle}</h2></div>
        <div className="sn-feature-grid">
          <article className="sn-feature sn-feature-wide"><div className="sn-icon"><FontAwesomeIcon icon={faShareNodes} /></div><div><h3>{c.privateTitle}</h3><p>{c.privateBody}</p></div><div className="sn-link-demo"><span>snappin.cloud/e/fiesta-lola</span><FontAwesomeIcon icon={faCheck} /></div></article>
          <article className="sn-feature sn-feature-dark"><div className="sn-icon"><FontAwesomeIcon icon={faBolt} /></div><h3>{c.instantTitle}</h3><p>{c.instantBody}</p><div className="sn-upload-demo"><FontAwesomeIcon icon={faCloudArrowUp} /><span>IMG_8241.jpg</span><b>100%</b></div></article>
          <article className="sn-feature sn-feature-photo"><div className="sn-feature-photo-image" /><div><div className="sn-icon"><FontAwesomeIcon icon={faImages} /></div><h3>{c.ownTitle}</h3><p>{c.ownBody}</p></div></article>
        </div>
      </section>

      <section className="sn-section sn-ai">
        <div className="sn-ai-copy"><p className="sn-eyebrow">{c.aiEyebrow}</p><h2>{c.aiTitle}</h2><p>{c.aiBody}</p><Link to="/events/dashboard" className="sn-button sn-button-light">{c.aiCta}<FontAwesomeIcon icon={faWandMagicSparkles} /></Link></div>
        <div className="sn-ai-visual">
          <div className="sn-ai-canvas"><img src={rooftopEvent} alt="Rooftop event being enhanced" /><div className="sn-ai-scan" /><div className="sn-ai-controls"><span><FontAwesomeIcon icon={faWandMagicSparkles} /> Smart edit</span><div><i/><i/><i/></div></div></div>
          <div className="sn-ai-pill"><FontAwesomeIcon icon={faWandMagicSparkles} /> AI edit applied</div>
        </div>
      </section>

      <section className="sn-section sn-screens">
        <div className="sn-screens-heading"><p className="sn-eyebrow">{c.galleryEyebrow}</p><h2>{c.galleryTitle}</h2></div>
        <div className="sn-flow-grid">
          <article><span>01</span><FontAwesomeIcon icon={faQrcode} /><strong>{steps[0].title}</strong><p>{steps[0].description}</p></article>
          <article className="sn-flow-featured"><span>02</span><FontAwesomeIcon icon={faCloudArrowUp} /><strong>{steps[1].title}</strong><div className="sn-flow-upload"><i/><b>IMG_8241.JPG</b><small>100%</small></div></article>
          <article><span>03</span><FontAwesomeIcon icon={faDownload} /><strong>{steps[2].title}</strong><p>{steps[2].description}</p></article>
        </div>
      </section>
    </main>
  );
};

export default HeroOne;
